import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Reply, Check, X, Trash2, User, Clock } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CommentsViewer = ({ project, onUpdate }) => {
  const [showNewComment, setShowNewComment] = useState(false);
  const [newComment, setNewComment] = useState({
    author_name: '',
    author_email: '',
    content: '',
    section: 'general'
  });
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  
  const comments = project.comments || [];
  
  // Get top-level comments (not replies)
  const topLevelComments = comments.filter(c => !c.parent_comment_id);
  
  // Get replies for a comment
  const getReplies = (commentId) => {
    return comments.filter(c => c.parent_comment_id === commentId);
  };
  
  const sections = [
    { id: 'general', label: 'General' },
    { id: 'basic_info', label: 'Basic Information' },
    { id: 'schedules', label: 'Schedules' },
    { id: 'general_notes', label: 'General Notes' },
    { id: 'compliance', label: 'Compliance' },
    { id: 'renders', label: 'Renders' }
  ];
  
  const handleAddComment = async () => {
    if (!newComment.author_name || !newComment.content) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await api.addComment(project.id, newComment);
      toast.success('Comment added');
      setNewComment({
        author_name: newComment.author_name,
        author_email: newComment.author_email,
        content: '',
        section: 'general'
      });
      setShowNewComment(false);
      onUpdate();
    } catch (error) {
      toast.error('Error adding comment');
      console.error('Error:', error);
    }
  };
  
  const handleReply = async (parentCommentId) => {
    if (!replyContent.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    
    try {
      await api.addComment(project.id, {
        author_name: newComment.author_name || 'Anonymous',
        author_email: newComment.author_email,
        content: replyContent,
        parent_comment_id: parentCommentId,
        section: comments.find(c => c.id === parentCommentId)?.section
      });
      
      toast.success('Reply added');
      setReplyContent('');
      setReplyTo(null);
      onUpdate();
    } catch (error) {
      toast.error('Error adding reply');
    }
  };
  
  const handleResolveComment = async (commentId, resolved) => {
    try {
      await api.updateComment(project.id, commentId, { resolved });
      toast.success(resolved ? 'Comment marked as resolved' : 'Comment marked as unresolved');
      onUpdate();
    } catch (error) {
      toast.error('Error updating comment');
    }
  };
  
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await api.deleteComment(project.id, commentId);
      toast.success('Comment deleted');
      onUpdate();
    } catch (error) {
      toast.error('Error deleting comment');
    }
  };
  
  const CommentCard = ({ comment, isReply = false }) => {
    const replies = getReplies(comment.id);
    
    return (
      <Card className={`p-4 ${isReply ? 'ml-8 mt-3 bg-slate-50 dark:bg-slate-800/50' : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3 flex-1">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className="font-semibold text-slate-900 dark:text-white">
                  {comment.author_name}
                </span>
                {comment.section && (
                  <Badge variant="outline" className="text-xs">
                    {sections.find(s => s.id === comment.section)?.label || comment.section}
                  </Badge>
                )}
                {comment.resolved && (
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    Resolved
                  </Badge>
                )}
              </div>
              <div className="flex items-center space-x-2 text-xs text-slate-500">
                <Clock className="h-3 w-3" />
                <span>{format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            {!comment.resolved && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleResolveComment(comment.id, true)}
                className="text-green-600 hover:text-green-700"
              >
                <Check className="h-4 w-4" />
              </Button>
            )}
            
            {comment.resolved && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleResolveComment(comment.id, false)}
                className="text-amber-600 hover:text-amber-700"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteComment(comment.id)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap mb-3">
          {comment.content}
        </p>
        
        {!isReply && (
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setReplyTo(comment.id)}
              className="text-blue-600"
            >
              <Reply className="mr-1 h-3 w-3" />
              Reply
            </Button>
            {replies.length > 0 && (
              <span className="text-xs text-slate-500">
                {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
              </span>
            )}
          </div>
        )}
        
        {replyTo === comment.id && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
            <Textarea
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Write your reply..."
              rows={3}
              className="mb-2"
            />
            <div className="flex justify-end space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setReplyTo(null);
                  setReplyContent('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => handleReply(comment.id)}
                className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
              >
                <Send className="mr-1 h-3 w-3" />
                Send Reply
              </Button>
            </div>
          </div>
        )}
        
        {/* Replies */}
        {replies.length > 0 && (
          <div className="mt-3 space-y-3">
            {replies.map(reply => (
              <CommentCard key={reply.id} comment={reply} isReply={true} />
            ))}
          </div>
        )}
      </Card>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Add Comment Section */}
      <Card className="p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
              Discussion & Comments
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Collaborate with your team on project details
            </p>
          </div>
          
          <Button
            onClick={() => setShowNewComment(!showNewComment)}
            className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            New Comment
          </Button>
        </div>
        
        {showNewComment && (
          <div className="mt-4 space-y-3 p-4 bg-white dark:bg-slate-800 rounded-lg">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="name">Your Name *</Label>
                <Input
                  id="name"
                  value={newComment.author_name}
                  onChange={(e) => setNewComment({ ...newComment, author_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label htmlFor="email">Email (optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={newComment.author_email}
                  onChange={(e) => setNewComment({ ...newComment, author_email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="section">Section</Label>
              <select
                id="section"
                value={newComment.section}
                onChange={(e) => setNewComment({ ...newComment, section: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {sections.map(section => (
                  <option key={section.id} value={section.id}>{section.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label htmlFor="comment">Comment *</Label>
              <Textarea
                id="comment"
                value={newComment.content}
                onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                placeholder="Write your comment or question..."
                rows={4}
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button
                variant="ghost"
                onClick={() => setShowNewComment(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddComment}
                className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
              >
                <Send className="mr-2 h-4 w-4" />
                Post Comment
              </Button>
            </div>
          </div>
        )}
      </Card>
      
      {/* Comments List */}
      {topLevelComments.length === 0 ? (
        <Card className="p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
          <MessageSquare className="mx-auto h-12 w-12 text-slate-400 mb-4" />
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            No comments yet. Start the discussion!
          </p>
          <Button
            variant="outline"
            onClick={() => setShowNewComment(true)}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            Add First Comment
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              All Comments ({topLevelComments.length})
            </h3>
            <div className="flex items-center space-x-2 text-sm text-slate-600">
              <span>
                {comments.filter(c => c.resolved).length} resolved
              </span>
              <span>•</span>
              <span>
                {comments.filter(c => !c.resolved).length} open
              </span>
            </div>
          </div>
          
          {topLevelComments.map(comment => (
            <CommentCard key={comment.id} comment={comment} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsViewer;